/**
 * Bibliothèque d'angles de post LinkedIn : pure, sans React.
 *
 * Aucune de ces structures n'est une règle de LinkedIn, et aucune ne prédit une
 * portée : ce sont des repères éditoriaux NeverZero. Le filtre est strict sur la
 * preuve dont on dispose (on ne propose pas un « avant/après chiffré » à qui n'a
 * aucun résultat), puis sur l'objectif ; le rôle sert à classer et à avertir.
 */

export type Role = 'fondateur' | 'consultant' | 'salarie' | 'recruteur';
export type Goal = 'leads' | 'autorite' | 'recrutement' | 'visibilite';
export type Proof = 'resultat' | 'erreur' | 'methode' | 'opinion';

export const ROLES: { id: Role; label: string }[] = [
  { id: 'fondateur', label: 'Fondateur ou dirigeant' },
  { id: 'consultant', label: 'Consultant ou indépendant' },
  { id: 'salarie', label: 'Salarié' },
  { id: 'recruteur', label: 'Recruteur ou RH' },
];

export const GOALS: { id: Goal; label: string }[] = [
  { id: 'leads', label: 'Trouver des clients' },
  { id: 'autorite', label: 'Être reconnu sur mon sujet' },
  { id: 'recrutement', label: 'Recruter' },
  { id: 'visibilite', label: 'Être plus visible' },
];

export const PROOFS: { id: Proof; label: string; hint: string }[] = [
  { id: 'resultat', label: 'Un résultat', hint: 'un client, un chiffre, un projet abouti' },
  { id: 'erreur', label: 'Une erreur', hint: 'un échec dont vous avez tiré une règle' },
  { id: 'methode', label: 'Une méthode', hint: 'une façon de faire que vous répétez' },
  { id: 'opinion', label: 'Une opinion', hint: 'un avis argumenté, même minoritaire' },
];

export interface Angle {
  id: string;
  name: string;
  proof: Proof;
  goals: Goal[];
  roles: Role[];
  /** `{sujet}` est remplacé par le sujet saisi ; les `[crochets]` restent à remplir. */
  hook: string;
  structure: string[];
  /** L'échec typique de cet angle, tel qu'on le voit dans les posts publiés. */
  trap: string;
}

const ALL_ROLES: Role[] = ['fondateur', 'consultant', 'salarie', 'recruteur'];

export const ANGLES: Angle[] = [
  // ---------------------------------------------------------------- résultat
  {
    id: 'avant-apres',
    name: 'Avant / après chiffré',
    proof: 'resultat',
    goals: ['leads', 'autorite'],
    roles: ['fondateur', 'consultant'],
    hook: '{sujet} : de [chiffre de départ] à [chiffre d\'arrivée] en [durée].',
    structure: [
      'Le point de départ en une phrase, avec son chiffre.',
      'Les deux ou trois décisions qui ont changé le résultat.',
      'Ce qui n\'a pas marché en chemin.',
      'À qui cette méthode s\'applique, et à qui elle ne s\'applique pas.',
    ],
    trap: 'Un résultat sans point de départ ni durée ne prouve rien. Et un chiffre client publié sans son accord est un risque, pas une preuve.',
  },
  {
    id: 'etude-de-cas-courte',
    name: 'L\'étude de cas en dix lignes',
    proof: 'resultat',
    goals: ['leads'],
    roles: ['consultant', 'fondateur'],
    hook: 'Un client est venu avec un problème : {sujet}. Voici ce qu\'on a changé, dans l\'ordre.',
    structure: [
      'Le problème du client, dans ses mots à lui.',
      'Ce que vous avez regardé en premier, et pourquoi.',
      'Le changement décisif et son effet mesuré.',
      'Ce que vous ne referiez pas.',
    ],
    trap: 'Raconter votre prestation au lieu du problème du client. Le lecteur cherche sa situation dans le récit, pas votre agenda.',
  },
  {
    id: 'resultat-modeste',
    name: 'Le petit résultat honnête',
    proof: 'resultat',
    goals: ['autorite', 'visibilite', 'recrutement'],
    roles: ALL_ROLES,
    hook: 'Ce n\'est pas un grand chiffre, mais {sujet} nous a rapporté [résultat] en [durée].',
    structure: [
      'Le chiffre, sans l\'enrober.',
      'Ce qui l\'explique vraiment, y compris la part de chance.',
      'Ce que vous en concluez, et ce que vous testez ensuite.',
    ],
    trap: 'Gonfler le résultat pour sembler crédible. Un lecteur du métier repère l\'arrondi généreux, et la crédibilité s\'arrête là.',
  },
  {
    id: 'temoignage-reformule',
    name: 'La phrase du client, mot pour mot',
    proof: 'resultat',
    goals: ['leads'],
    roles: ['fondateur', 'consultant'],
    hook: 'Ce que [prénom, poste] m\'a dit après {sujet} : « [citation exacte] ».',
    structure: [
      'La citation seule, sur sa propre ligne.',
      'Le contexte : ce qui avait précédé, en deux phrases.',
      'Ce que vous avez fait de concret pour en arriver là.',
    ],
    trap: 'Une citation floue (« super collaboration ») ne dit rien. Gardez celle qui contient un fait ou un chiffre, avec l\'accord de la personne.',
  },
  {
    id: 'parcours-collegue',
    name: 'Le parcours d\'un collègue',
    proof: 'resultat',
    goals: ['recrutement', 'visibilite'],
    roles: ['recruteur', 'salarie', 'fondateur'],
    hook: 'Il y a [durée], [prénom] est arrivé comme [poste]. Aujourd\'hui, {sujet}.',
    structure: [
      'Le point d\'arrivée de la personne, puis son point de départ.',
      'Deux moments précis qui ont fait la différence.',
      'Ce que l\'équipe a mis en place pour que ça arrive.',
      'Le type de profil qui trouverait sa place ici.',
    ],
    trap: 'Les louanges génériques. Citez des faits et demandez l\'accord de la personne avant de publier.',
  },
  {
    id: 'poste-ouvert',
    name: 'Ce qu\'on a changé dans l\'annonce',
    proof: 'resultat',
    goals: ['recrutement'],
    roles: ['recruteur', 'fondateur'],
    hook: 'Le poste de [intitulé] était ouvert depuis [durée]. Voici ce qu\'on a changé dans l\'annonce.',
    structure: [
      'L\'ancienne formulation, citée telle quelle.',
      'Ce qui a été réécrit : missions réelles, fourchette de salaire, première semaine.',
      'Le résultat sur le nombre et la qualité des candidatures.',
    ],
    trap: 'Une annonce déguisée en récit, sans le contenu du poste ni la rémunération. Les candidats le voient au deuxième paragraphe.',
  },
  {
    id: 'part-equipe',
    name: 'Ma part dans le résultat de l\'équipe',
    proof: 'resultat',
    goals: ['visibilite', 'autorite'],
    roles: ['salarie'],
    hook: 'Notre équipe a [résultat] sur {sujet}. Ma part là-dedans : [rôle précis].',
    structure: [
      'Le résultat de l\'équipe, avec l\'accord de l\'employeur s\'il est chiffré.',
      'Votre rôle exact, en une phrase, sans « nous » flou.',
      'Ce que vous avez appris que vous n\'auriez pas appris seul.',
    ],
    trap: 'Dire « nous » partout, ou publier un chiffre interne confidentiel. Le premier ne montre rien de vous, le second peut coûter plus cher que le post ne rapporte.',
  },
  // ------------------------------------------------------------------ erreur
  {
    id: 'erreur-couteuse',
    name: 'L\'erreur qui a coûté cher',
    proof: 'erreur',
    goals: ['autorite', 'visibilite', 'leads'],
    roles: ['fondateur', 'consultant', 'salarie'],
    hook: 'J\'ai perdu [coût ou durée] à cause de {sujet}.',
    structure: [
      'Ce que vous pensiez à l\'époque, et pourquoi c\'était raisonnable.',
      'Ce qui s\'est passé, avec le coût réel.',
      'La règle que vous appliquez depuis.',
    ],
    trap: 'La confession sans leçon. Le lecteur doit repartir avec une règle qu\'il peut appliquer lundi, pas avec de la compassion.',
  },
  {
    id: 'refaire-depuis-zero',
    name: 'Ce que je ferais autrement',
    proof: 'erreur',
    goals: ['autorite', 'leads', 'recrutement'],
    roles: ['consultant', 'fondateur'],
    hook: 'Si je devais refaire {sujet} depuis zéro, je ne ferais pas [ce que vous avez fait].',
    structure: [
      'Ce que vous avez fait, et le contexte qui l\'expliquait.',
      'Ce que vous feriez à la place, avec une raison.',
      'Le signal qui vous aurait alerté plus tôt.',
    ],
    trap: 'Une liste de regrets vagues (« j\'aurais dû me lancer plus tôt »). Un seul regret, précis, vaut mieux que cinq.',
  },
  {
    id: 'erreur-des-clients',
    name: 'L\'erreur que je vois chez presque tous',
    proof: 'erreur',
    goals: ['leads', 'autorite'],
    roles: ['consultant', 'fondateur'],
    hook: 'L\'erreur que je vois chez presque tous ceux qui se lancent dans {sujet} : [erreur].',
    structure: [
      'L\'erreur, nommée sans détour.',
      'Pourquoi elle est tentante.',
      'Ce qu\'elle coûte quand on la découvre trop tard.',
      'Le correctif le plus simple.',
    ],
    trap: 'Pointer un client reconnaissable. Parlez d\'un schéma observé, jamais d\'une entreprise identifiable.',
  },
  {
    id: 'erreur-recrutement',
    name: 'Ma pire erreur de recrutement',
    proof: 'erreur',
    goals: ['recrutement', 'autorite'],
    roles: ['recruteur', 'fondateur'],
    hook: 'Ma pire erreur de recrutement : {sujet}.',
    structure: [
      'Ce que vous avez vu en entretien et ce que vous avez cru en déduire.',
      'Ce qui s\'est passé les trois premiers mois.',
      'La question ou l\'exercice que vous avez ajouté depuis.',
    ],
    trap: 'Dénigrer la personne recrutée. L\'erreur est celle du processus, et c\'est le processus qu\'on raconte.',
  },
  {
    id: 'bourde-debut',
    name: 'La bourde des premières semaines',
    proof: 'erreur',
    goals: ['visibilite', 'recrutement'],
    roles: ['salarie', 'recruteur'],
    hook: 'Ma première semaine chez [entreprise], j\'ai fait {sujet}.',
    structure: [
      'Ce que vous avez fait, avec une pointe d\'humour.',
      'La réaction de l\'équipe, ce qui montre la culture.',
      'Ce que vous en avez retenu.',
    ],
    trap: 'Critiquer l\'employeur ou un collègue, même à demi-mot. La bourde se raconte contre soi-même.',
  },
  {
    id: 'jetais-dans-le-faux',
    name: 'J\'avais tort',
    proof: 'erreur',
    goals: ['autorite', 'visibilite'],
    roles: ALL_ROLES,
    hook: 'Il y a [durée], je disais que {sujet}. J\'avais tort.',
    structure: [
      'Ce que vous défendiez, citation à l\'appui si elle existe.',
      'Le fait ou l\'expérience qui vous a fait changer d\'avis.',
      'Ce que vous recommandez maintenant.',
    ],
    trap: 'Le faux revirement, où l\'on avait « presque raison ». Un changement d\'avis ne vaut que s\'il coûte quelque chose à admettre.',
  },
  {
    id: 'test-rate',
    name: 'Le test qui n\'a pas marché',
    proof: 'erreur',
    goals: ['autorite', 'leads', 'visibilite'],
    roles: ['fondateur', 'consultant', 'salarie'],
    hook: 'On a testé {sujet} pendant [durée]. Résultat : [chiffre]. On arrête.',
    structure: [
      'L\'hypothèse de départ.',
      'Comment le test était monté, assez pour qu\'on le refasse.',
      'Le résultat brut.',
      'Ce que cela change dans vos décisions.',
    ],
    trap: 'Conclure trop large d\'un test trop petit. Dites la taille de l\'échantillon et ce que le test ne prouve pas.',
  },
  // ----------------------------------------------------------------- méthode
  {
    id: 'etapes',
    name: 'Ma méthode en N étapes',
    proof: 'methode',
    goals: ['leads', 'autorite'],
    roles: ['consultant', 'fondateur'],
    hook: 'Ma méthode en [N] étapes pour {sujet} :',
    structure: [
      'Les étapes numérotées, une ligne chacune.',
      'Un exemple réel pour l\'étape la moins évidente.',
      'La question qui montre si vous en avez besoin.',
    ],
    trap: 'Des étapes interchangeables (« analysez, planifiez, exécutez »). Si elles valent pour n\'importe quel sujet, supprimez-les.',
  },
  {
    id: 'coulisses',
    name: 'Les coulisses d\'une journée',
    proof: 'methode',
    goals: ['visibilite', 'recrutement'],
    roles: ['salarie', 'recruteur', 'fondateur'],
    hook: 'Une journée type quand on travaille sur {sujet}, heure par heure.',
    structure: [
      'Trois ou quatre créneaux précis, avec ce qui s\'y passe.',
      'Un moment qui ne se passe jamais comme prévu.',
      'Ce que cette journée dit de la façon de travailler de l\'équipe.',
    ],
    trap: 'Un journal sans enjeu. Choisissez la journée où quelque chose a coincé, pas celle où tout était fluide.',
  },
  {
    id: 'modele-partage',
    name: 'Le modèle que j\'utilise, donné',
    proof: 'methode',
    goals: ['leads', 'visibilite'],
    roles: ['consultant', 'fondateur', 'recruteur'],
    hook: 'Le modèle que j\'utilise pour {sujet}, tel quel.',
    structure: [
      'Le modèle lui-même, dans le post ou en document joint.',
      'Un exemple rempli.',
      'Comment l\'adapter, et quand il ne convient pas.',
    ],
    trap: 'Promettre le modèle « en commentaire » pour gonfler l\'engagement. Donnez-le directement : c\'est le contenu, pas l\'appât.',
  },
  {
    id: 'grille-evaluation',
    name: 'Comment nous évaluons',
    proof: 'methode',
    goals: ['recrutement', 'autorite'],
    roles: ['recruteur', 'fondateur'],
    hook: 'Les [N] critères sur lesquels nous évaluons {sujet}, et ce qui ne compte pas.',
    structure: [
      'Les critères, avec ce qu\'on regarde concrètement pour chacun.',
      'Ce qui ne pèse pas dans la décision, et qu\'on voit pourtant partout.',
      'Un exemple de réponse qui passe et un qui ne passe pas.',
    ],
    trap: 'Des critères flous (« savoir-être », « motivation »). Un candidat doit pouvoir se préparer avec votre post.',
  },
  {
    id: 'outil-quotidien',
    name: 'L\'outil qui m\'a fait gagner du temps',
    proof: 'methode',
    goals: ['visibilite', 'autorite'],
    roles: ['salarie', 'consultant'],
    hook: 'L\'outil qui m\'a fait gagner [durée] par semaine sur {sujet}.',
    structure: [
      'Le problème d\'avant, chiffré.',
      'Ce que fait l\'outil, en deux phrases.',
      'Le réglage ou l\'usage non évident.',
      'Sa limite.',
    ],
    trap: 'La publicité déguisée. Sans limite citée, personne ne vous croit.',
  },
  {
    id: 'premiere-question',
    name: 'La première question que je pose',
    proof: 'methode',
    goals: ['leads', 'autorite'],
    roles: ['consultant', 'fondateur'],
    hook: 'La première question que je pose avant de travailler sur {sujet} : « [question] ».',
    structure: [
      'La question, seule sur sa ligne.',
      'Pourquoi elle change la recommandation.',
      'Deux réponses possibles et ce que chacune implique.',
    ],
    trap: 'Une question dont la réponse ne change rien à ce que vous feriez. Elle doit orienter une vraie décision.',
  },
  {
    id: 'lire-une-offre',
    name: 'Lire entre les lignes d\'une offre',
    proof: 'methode',
    goals: ['recrutement', 'visibilite'],
    roles: ['recruteur', 'salarie'],
    hook: 'Ce que veut vraiment dire une offre d\'emploi sur {sujet}.',
    structure: [
      'Trois formulations courantes et leur traduction.',
      'Ce qu\'un candidat peut demander pour en savoir plus.',
      'Ce que vous écrivez vous-même à la place.',
    ],
    trap: 'Le ton moqueur envers les autres entreprises. Montrez votre version, pas la caricature des leurs.',
  },
  // ----------------------------------------------------------------- opinion
  {
    id: 'contre-pied',
    name: 'Le contre-pied argumenté',
    proof: 'opinion',
    goals: ['autorite', 'visibilite', 'leads', 'recrutement'],
    roles: ALL_ROLES,
    hook: 'On vous dit que {sujet}. C\'est incomplet, et voici pourquoi.',
    structure: [
      'L\'idée reçue, formulée comme ses défenseurs la formulent.',
      'Le cas où elle est vraie, pour être honnête.',
      'Le cas où elle échoue, avec un exemple.',
      'Ce que vous faites à la place.',
    ],
    trap: 'Le contre-pied contre un adversaire imaginaire. Si personne ne dit vraiment ce que vous réfutez, l\'opposition est vide.',
  },
  {
    id: 'prediction-datee',
    name: 'La prédiction datée',
    proof: 'opinion',
    goals: ['autorite', 'visibilite', 'leads'],
    roles: ['fondateur', 'consultant'],
    hook: 'D\'ici [échéance], {sujet} aura changé. Voici ce que je ferais dès maintenant.',
    structure: [
      'Ce que vous observez aujourd\'hui, en faits.',
      'Ce que vous en déduisez, avec une échéance.',
      'Ce que vous feriez maintenant, et ce qui vous ferait changer d\'avis.',
    ],
    trap: 'Une prédiction qu\'on ne pourra jamais vérifier. Datez-la : dans un an, vous pourrez la relire en public.',
  },
  {
    id: 'secteur-tabou',
    name: 'Ce que mon métier n\'ose pas dire',
    proof: 'opinion',
    goals: ['autorite', 'leads'],
    roles: ['fondateur', 'consultant'],
    hook: 'Ce que personne ne dit sur {sujet} dans mon métier : [constat].',
    structure: [
      'Le constat, en une phrase.',
      'Trois observations qui l\'appuient.',
      'Ce que cela change pour un acheteur ou un client.',
    ],
    trap: 'Une provocation sans argument, ou une attaque contre une entreprise nommée. Gardez le constat, supprimez la cible.',
  },
  {
    id: 'actualite',
    name: 'Mon avis sur l\'actualité du secteur',
    proof: 'opinion',
    goals: ['visibilite', 'autorite', 'leads'],
    roles: ALL_ROLES,
    hook: '[Événement récent] montre que {sujet}. Mon avis en [N] points.',
    structure: [
      'Le fait, en une phrase, avec sa source.',
      'Ce que les commentaires dominants oublient.',
      'Votre lecture, avec un exemple tiré de votre pratique.',
    ],
    trap: 'Arriver après tout le monde en répétant la même chose. Sans un angle que les autres posts n\'ont pas, ne publiez pas.',
  },
  {
    id: 'ce-que-nous-refusons',
    name: 'Ce que nous refusons de faire',
    proof: 'opinion',
    goals: ['recrutement', 'autorite'],
    roles: ['recruteur', 'fondateur'],
    hook: 'Une pratique que nous avons arrêtée dans nos recrutements : {sujet}.',
    structure: [
      'La pratique, et pourquoi tant de monde la garde.',
      'Ce qui vous a fait arrêter.',
      'Ce que vous faites à la place, et le coût.',
    ],
    trap: 'Une posture sans pratique derrière. Citez ce qui a réellement changé dans votre processus.',
  },
  {
    id: 'conseil-debutant',
    name: 'Le conseil que personne ne donne',
    proof: 'opinion',
    goals: ['visibilite', 'recrutement', 'autorite'],
    roles: ['salarie', 'recruteur', 'consultant'],
    hook: 'Conseil à ceux qui débutent dans {sujet} : [le conseil que peu de gens donnent].',
    structure: [
      'Le conseil, seul, sur une ligne.',
      'Pourquoi on ne le donne pas : il demande un effort ou un renoncement.',
      'L\'exemple de quelqu\'un qui l\'a suivi, vous inclus.',
    ],
    trap: 'Le conseil de survivant : « j\'ai réussi en faisant X, faites X ». Dites ce qui aurait pu mal tourner.',
  },
  {
    id: 'question-ouverte',
    name: 'La question à laquelle vous n\'avez pas la réponse',
    proof: 'opinion',
    goals: ['visibilite', 'recrutement', 'leads'],
    roles: ALL_ROLES,
    hook: 'Je n\'ai pas de réponse sur {sujet} : [question précise]. Voici ce que je sais déjà.',
    structure: [
      'La question, étroite et précise.',
      'Ce que vous avez déjà essayé ou lu.',
      'Votre hypothèse actuelle, qu\'on peut contredire.',
    ],
    trap: 'Une question trop large (« que pensez-vous de l\'IA ? ») et sans avis de votre part. Sans hypothèse, vous ne lancez pas un débat, vous demandez qu\'on travaille à votre place.',
  },
];

export const PAGE_SIZE = 5;

export interface RankedAngle {
  angle: Angle;
  roleFit: boolean;
  goalHits: number;
}

export interface Selection {
  role: Role | null;
  goal: Goal | null;
  /** Vide : aucune preuve choisie, on ne filtre pas dessus. */
  proofs: Proof[];
}

/**
 * Filtre strict sur la preuve (si elle est précisée) et sur l'objectif (si
 * précisé), classement par adéquation au rôle, puis par nombre d'objectifs
 * couverts (un angle taillé pour un seul objectif passe avant un angle fourre-tout).
 */
export function selectAngles(selection: Selection): RankedAngle[] {
  const ranked: RankedAngle[] = [];
  for (const angle of ANGLES) {
    if (selection.proofs.length > 0 && !selection.proofs.includes(angle.proof)) continue;
    if (selection.goal && !angle.goals.includes(selection.goal)) continue;
    ranked.push({
      angle,
      roleFit: selection.role ? angle.roles.includes(selection.role) : true,
      goalHits: angle.goals.length,
    });
  }
  return ranked.sort((x, y) => Number(y.roleFit) - Number(x.roleFit) || x.goalHits - y.goalHits);
}

const ROLE_NOTES: Record<Role, string> = {
  fondateur:
    'Un chiffre d\'affaires, un nom de client ou une fourchette de prix se publie avec l\'accord des personnes concernées. Une fourchette suffit souvent à prouver.',
  consultant:
    'Anonymisez le client (secteur et taille, pas le nom) sauf accord écrit. Les détails d\'un contrat sont souvent couverts par une clause de confidentialité.',
  salarie:
    'Vous parlez en votre nom, pas en celui de l\'entreprise. Vérifiez la politique de communication de votre employeur avant de citer un chiffre interne ou un client.',
  recruteur:
    'Ne décrivez jamais un candidat identifiable, même en bien. Parlez du processus et de ce que vous avez changé, pas de la personne.',
};

const COMBO_NOTES: { role: Role; goal: Goal; note: string }[] = [
  {
    role: 'salarie',
    goal: 'leads',
    note: 'Un salarié qui cherche des clients publie pour son employeur : mettez-vous d\'accord avec lui avant, et privilégiez les angles de méthode ou d\'opinion.',
  },
  {
    role: 'recruteur',
    goal: 'leads',
    note: 'Un recruteur qui cherche des clients parle à des directions, pas à des candidats : les angles de résultat doivent porter sur ce que vous avez permis, pas sur ce que vous avez placé.',
  },
];

/** Les mises en garde qui s'appliquent à cette combinaison, la plus spécifique en premier. */
export function guardrails(selection: Selection): string[] {
  const notes: string[] = [];
  if (selection.role) {
    const combo = COMBO_NOTES.find((entry) => entry.role === selection.role && entry.goal === selection.goal);
    if (combo) notes.push(combo.note);
    notes.push(ROLE_NOTES[selection.role]);
  }
  return notes;
}

export function fillHook(hook: string, topic: string): string {
  const clean = topic.trim();
  const filled = hook.replace(/\{sujet\}/g, clean || '[votre sujet]');
  return filled.charAt(0).toUpperCase() + filled.slice(1);
}

export function angleText(angle: Angle, topic: string): string {
  const lines = [
    fillHook(angle.hook, topic),
    '',
    ...angle.structure.map((step, index) => `${index + 1}. ${step}`),
    '',
    `Le piège : ${angle.trap}`,
  ];
  return lines.join('\n');
}
